import ExcelJS from 'exceljs'
import { formatCompetencia } from './format'

export async function exportRetificacaoExcelSimples(analises: any) {
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('Planilha de Retificação')

  // Linha de identificação do cliente
  const info = `${analises.razao_social} - ${analises.cnpj}`
  ws.mergeCells('A1:I1')
  const cellInfo = ws.getCell('A1')
  cellInfo.value = info
  cellInfo.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } }
  cellInfo.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4B4B4B' } }
  cellInfo.alignment = { vertical: 'middle', horizontal: 'left' }
  ws.getRow(1).height = 20

  // Cabeçalhos
  const headers = [
    'Competência',
    'Receita Bruta',
    'Receita s/ ST/MONO',
    'Receita c/ ST/MONO',
    'Retificado ST',
    'Receita Monofásico',
    'Total Apurado',
    'Total a Restituir',
    'Diagnóstico'
  ]
  ws.addRow(headers)

  // Estilo cabeçalho
  ws.getRow(2).font = { bold: true, color: { argb: 'FFFFFFFF' } }
  ws.getRow(2).eachCell(cell => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4E79' } } // Azul forte
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
  })

  // Adiciona dados
  analises.analises.forEach((row: any, idx: number) => {
    const linha = ws.addRow([
      formatCompetencia(row.competencia),
      Number(row.rb_auferida) || 0,
      Number(row.sem_subs_rb) || 0,
      Number(row.com_subs_rb) || 0,
      Number(row.sugerido_st) || 0,
      Number(row.sugerido_mono_st) || 0,
      Number(row.total) || 0,
      Number(row.total_a_restituir) || 0,
      row.diagnostico ?? ''
    ])

    // Linhas alternadas
    const fillColor = idx % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF'
    linha.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } }
    })
  })

  // Largura das colunas

  // Largura das colunas
  const widths = [20, 20, 22, 22, 18, 22, 18, 22, 80]
  widths.forEach((w, i) => (ws.getColumn(i + 1).width = w))

  // Formatação numérica (R$) colunas 2..8
  for (let c = 2; c <= 8; c++) {
    ws.getColumn(c).numFmt = '"R$"#,##0.00'
  }

  // Congela info + cabeçalho
  ws.views = [{ state: 'frozen', ySplit: 2 }]

  // === Bordas em todas as células usadas (full grid) ===
  const lastRow = ws.lastRow?.number ?? 2
  const lastCol = 9
  const thinBorder = {
    top: { style: 'thin', color: { argb: 'FFBFBFBF' } },
    left: { style: 'thin', color: { argb: 'FFBFBFBF' } },
    bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } },
    right: { style: 'thin', color: { argb: 'FFBFBFBF' } }
  } as const

  // Borda também na célula mesclada A1:I1
  ws.getCell('A1').border = thinBorder

  for (let r = 2; r <= lastRow; r++) {
    const row = ws.getRow(r)
    for (let c = 1; c <= lastCol; c++) {
      const cell = row.getCell(c)
      cell.border = thinBorder
      // garantia de centralização (inclusive se algo for adicionado depois)
      const prev = cell.alignment || {}
      cell.alignment = { ...prev, horizontal: 'center', vertical: 'middle', wrapText: true }
    }
  }

  // Gera o arquivo para download
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${analises.razao_social} - ${analises.cnpj}_${new Date().toISOString().slice(0, 10)}.xlsx`
  a.click()
  URL.revokeObjectURL(url)
}

type LinhaAnalise = {
  competencia: string
  rb_auferida?: number
  total_produtos_validos_xml?: number
  sem_subs_rb?: number
  com_subs_rb?: number
  receita_st?: number
  receita_monofasico?: number
  receita_st_e_monofasico?: number
  porcentagem_monofasicos_xml?: number
  porcentagem_icms_xml?: number
  cofins_pago?: number
  pis_pago?: number
  cofins_devido?: number
  pis_devido?: number
  cofins_restituir?: number
  pis_restituir?: number
  sugerido_st?: number
  sugerido_mono_st?: number
  total_icms_st_validos_xml?: number
  total_monofasicos_validos_xml?: number
  total_icms_st_mono_validos_xml?: number
  red_efetiva?: number
  aliq_efetiva_c?: number // 0..1
  aliq_efetiva_p?: number // 0..1
  total?: number
  total_a_restituir?: number
  diagnostico?: string
}

export async function exportRetificacaoExcelSimplesDetalhado(analises: {
  razao_social: string
  cnpj: string
  analises: LinhaAnalise[]
}) {
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('Retificação - Detalhado')

  // Headers
  const headers = [
    'Competência',
    'Receita Bruta',
    'Receita Bruta (XML)',
    'Receita s/ ST/MONO',
    'Receita c/ ST/MONO',
    'Receita ST',
    'Receita Monofásico',
    'Receita ST + MONO',
    '% Monofásicos',
    '% ICMS ST',
    'COFINS Pago',
    'PIS Pago',
    'COFINS Devido',
    'PIS Devido',
    'COFINS a Restituir',
    'PIS a Restituir',
    'Retificado ST',
    'Total Monofásicos',
    'Total ICMS ST',
    'Total Produtos Válidos',
    'Total Monofásicos Válidos',
    'Total ICMS-ST Válidos',
    'Redução Efetiva',
    'Alíquota Efetiva COFINS',
    'Alíquota Efetiva PIS',
    'Total Apurado',
    'Total a Restituir',
    'Diagnóstico'
  ] as const

  // Util para pegar a letra da última coluna
  const colLetter = (n: number) => {
    let s = ''
    while (n > 0) {
      const m = (n - 1) % 26
      s = String.fromCharCode(65 + m) + s
      n = Math.floor((n - 1) / 26)
    }
    return s
  }
  const lastCol = colLetter(headers.length)

  // Linha identificação (mescla até a última coluna)
  const info = `${analises.razao_social} - ${analises.cnpj}`
  ws.mergeCells(`A1:${lastCol}1`)
  const cellInfo = ws.getCell('A1')
  cellInfo.value = info
  cellInfo.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } }
  cellInfo.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4B4B4B' } }
  cellInfo.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
  ws.getRow(1).height = 20

  // Cabeçalho
  ws.addRow(headers as unknown as any[])
  ws.getRow(2).font = { bold: true, color: { argb: 'FFFFFFFF' } }
  ws.getRow(2).height = 30
  ws.getRow(2).eachCell(cell => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F4E79' } }
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
  })

  // Dados
  ;(analises.analises ?? []).forEach((r: LinhaAnalise, idx: number) => {
    const linha = ws.addRow([
      formatCompetencia(r.competencia),
      n(r.rb_auferida),
      n(r.total_produtos_validos_xml),
      n(r.sem_subs_rb),
      n(r.com_subs_rb),
      n(r.receita_st),
      n(r.receita_monofasico),
      n(r.receita_st_e_monofasico),
      n(r.porcentagem_monofasicos_xml, true),
      n(r.porcentagem_icms_xml, true),
      n(r.cofins_pago),
      n(r.pis_pago),
      n(r.cofins_devido),
      n(r.pis_devido),
      n(r.cofins_restituir),
      n(r.pis_restituir),
      n(r.sugerido_st),
      n(r.sugerido_mono_st),
      n(r.total_icms_st_validos_xml),
      n(r.total_produtos_validos_xml),
      n(r.total_monofasicos_validos_xml),
      n(r.total_icms_st_mono_validos_xml),
      n(r.red_efetiva),
      n(r.aliq_efetiva_c, false, true),
      n(r.aliq_efetiva_p, false, true),
      n(r.total),
      n(r.total_a_restituir),
      r.diagnostico ?? ''
    ])

    const fillColor = idx % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF'
    linha.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } }
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
    })
  })

  // Larguras
  const widths = [
    14, 16, 18, 18, 18, 14, 18, 18, 14, 12, 14, 12, 16, 12, 18, 16, 16, 18, 16, 20, 22, 22, 16, 18, 16, 16, 18, 80
  ]
  widths.forEach((w, i) => (ws.getColumn(i + 1).width = w))

  // Formatação numérica
  // Moeda: 2..8, 11..22, 26..27 (exclui 24 e 25 que são %; inclui 23 Redução Efetiva)
  const moneyCols = [2, 3, 4, 5, 6, 7, 8, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 26, 27]
  moneyCols.forEach(c => {
    ws.getColumn(c).numFmt = '"R$"#,##0.00'
  })

  // % 0..100: 9,10  |  % 0..1: 24,25
  const pctCols_0to100 = [9, 10]
  const pctCols_0to1 = [24, 25]

  const firstDataRow = 3
  const lastRow = ws.lastRow?.number ?? 2

  // Converte 0..100 -> 0..1 nas colunas 9 e 10
  for (let r = firstDataRow; r <= lastRow; r++) {
    for (const c of pctCols_0to100) {
      const cell = ws.getRow(r).getCell(c)
      const v = typeof cell.value === 'number' ? cell.value : Number(cell.value)
      if (!isNaN(v)) cell.value = v / 100
    }
  }

  // Agora formata todas as colunas percentuais
  const percentCols = [...pctCols_0to100, ...pctCols_0to1]
  percentCols.forEach(c => {
    ws.getColumn(c).numFmt = '0.00%'
  })

  // Congela
  ws.views = [{ state: 'frozen', ySplit: 2 }]

  // Bordas (grid completo, incluindo linha 1)
  const thinBorder = {
    top: { style: 'thin', color: { argb: 'FFBFBFBF' } },
    left: { style: 'thin', color: { argb: 'FFBFBFBF' } },
    bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } },
    right: { style: 'thin', color: { argb: 'FFBFBFBF' } }
  } as const
  for (let c = 1; c <= headers.length; c++) ws.getRow(1).getCell(c).border = thinBorder
  for (let r = 2; r <= lastRow; r++) {
    const row = ws.getRow(r)
    for (let c = 1; c <= headers.length; c++) {
      const cell = row.getCell(c)
      cell.border = thinBorder
      const prev = cell.alignment || {}
      cell.alignment = { ...prev, horizontal: 'center', vertical: 'middle', wrapText: true }
    }
  }

  // Download
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${analises.razao_social} - ${analises.cnpj}_${new Date().toISOString().slice(0, 10)}-detalhado.xlsx`
  a.click()
  URL.revokeObjectURL(url)

  // util
  function n(val?: number, isPercent0to100 = false, isPercent0to1 = false) {
    if (val == null || isNaN(Number(val))) return 0
    const num = Number(val)
    if (isPercent0to100) return num
    if (isPercent0to1) return num
    return num
  }
}
